import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { HelpCircle } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { AiChatElicitationAnswer } from '@/domain/repositories/ai-chat-repository.js';

export type EfChatElicitationOption = { id: string; label: string };

export type EfChatElicitationQuestionView = {
    id: string;
    prompt: string;
    options: EfChatElicitationOption[];
    allowMultiple: boolean;
    allowFreeText: boolean;
};

type StepAnswer = { selectedIds: string[]; freeText: string };

export type EfChatElicitationPromptProps = {
    questions: EfChatElicitationQuestionView[];
    onAnswer: (answer: AiChatElicitationAnswer) => void;
    title: string;
    sendLabel: string;
    placeholder: string;
    nextLabel: string;
    backLabel: string;
    progressLabel: (current: number, total: number) => string;
};

const emptyStep: StepAnswer = { selectedIds: [], freeText: '' };

const isStepAnswered = (answer: StepAnswer): boolean => answer.selectedIds.length > 0 || answer.freeText.trim().length > 0;

export function EfChatElicitationPrompt({ questions, onAnswer, title, sendLabel, placeholder, nextLabel, backLabel, progressLabel }: EfChatElicitationPromptProps) {
    const [step, setStep] = useState(0);
    const [answers, setAnswers] = useState<Record<string, StepAnswer>>({});

    const total = questions.length;
    const current = questions[Math.min(step, total - 1)];
    const currentAnswer = current ? (answers[current.id] ?? emptyStep) : emptyStep;
    const allAnswered = useMemo(() => questions.every((question) => isStepAnswered(answers[question.id] ?? emptyStep)), [answers, questions]);

    if (!current) {
        return null;
    }

    const updateAnswer = (partial: Partial<StepAnswer>): void => {
        setAnswers((prev) => ({ ...prev, [current.id]: { ...(prev[current.id] ?? emptyStep), ...partial } }));
    };

    const toggleOption = (optionId: string): void => {
        const selected = currentAnswer.selectedIds;
        if (current.allowMultiple) {
            const next = selected.includes(optionId) ? selected.filter((id) => id !== optionId) : [...selected, optionId];
            updateAnswer({ selectedIds: next });
            return;
        }
        updateAnswer({ selectedIds: selected.includes(optionId) ? [] : [optionId] });
    };

    const handleSubmit = (): void => {
        const items = questions.map((question) => {
            const answer = answers[question.id] ?? emptyStep;
            const freeText = answer.freeText.trim();
            return {
                questionId: question.id,
                optionIds: answer.selectedIds.length > 0 ? answer.selectedIds : undefined,
                freeText: freeText.length > 0 ? freeText : undefined
            };
        });
        if (total === 1) {
            const only = items[0];
            onAnswer({ optionIds: only?.optionIds, freeText: only?.freeText, answers: items });
            return;
        }
        onAnswer({ answers: items });
    };

    const isLastStep = step >= total - 1;

    return (
        <Box
            data-testid="ef-chat-elicitation"
            sx={{
                border: '1px solid',
                borderColor: 'warning.light',
                bgcolor: (t) => t.palette.action.hover,
                borderRadius: 2,
                p: 1.5
            }}
        >
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: total > 1 ? 0.5 : 1 }}>
                <HelpCircle size={16} style={{ opacity: 0.7 }} />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{title}</Typography>
            </Stack>
            {total > 1 ? <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1 }}>{progressLabel(Math.min(step, total - 1) + 1, total)}</Typography> : null}
            {current.prompt ? <Typography sx={{ fontSize: 14, mb: 1.5, whiteSpace: 'pre-wrap' }}>{current.prompt}</Typography> : null}
            {current.options.length > 0 ? (
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: current.allowFreeText ? 1.5 : 1 }}>
                    {current.options.map((option) => {
                        const selected = currentAnswer.selectedIds.includes(option.id);
                        return (
                            <Button key={option.id} size="small" variant={selected ? 'contained' : 'outlined'} onClick={() => toggleOption(option.id)}>
                                {option.label}
                            </Button>
                        );
                    })}
                </Stack>
            ) : null}
            {current.allowFreeText ? (
                <TextField
                    fullWidth
                    size="small"
                    multiline
                    maxRows={4}
                    value={currentAnswer.freeText}
                    placeholder={placeholder}
                    onChange={(event) => updateAnswer({ freeText: event.target.value })}
                    sx={{ mb: 1 }}
                />
            ) : null}
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                {step > 0 ? (
                    <Button size="small" variant="text" onClick={() => setStep((value) => Math.max(0, value - 1))}>
                        {backLabel}
                    </Button>
                ) : null}
                {isLastStep ? (
                    <Button size="small" variant="contained" disabled={!allAnswered} onClick={handleSubmit}>
                        {sendLabel}
                    </Button>
                ) : (
                    <Button size="small" variant="contained" disabled={!isStepAnswered(currentAnswer)} onClick={() => setStep((value) => Math.min(total - 1, value + 1))}>
                        {nextLabel}
                    </Button>
                )}
            </Stack>
        </Box>
    );
}
